﻿import { SessionService } from "./session";
import { FIQAssist } from "../app";
import { Utils, DateTime } from "@fiqas/utils";
import { Injectable, Inject } from "@fiqas/angular-utils";
import { AuthenticationApiService, AccessTokenResponse } from "./authenticationApiService";
import { IsProcessingService } from "./isProcessingService";
import { FiqassistRole } from "../models/user";

const CREDENTIALSKEY = "credentials";
const TOKENUSER = "tokenUser";
const TOKENKEY = "token";
const TOKENEXPIRE = "tokenExpire";
const REFRESHKEY = "refresh";
const REFRESH_MARGIN = 300000; //ms;

@Injectable({
    module: FIQAssist,
    selector: AuthenticationService.selector
})
export class AuthenticationService {
    public static selector = "AuthenticationService"

    constructor(
        @Inject('$q') private $q: angular.IQService,
        @Inject() private api: AuthenticationApiService,
        @Inject() private session: SessionService,
        @Inject('$timeout') private $timeout: angular.ITimeoutService
    ) {
    }
    public async authenticate(credentials: { username: string, password: string }, persistent: boolean) {
        try {
            var response = await this.api.Authenticate(credentials);
            this.handleTokenResponse(credentials.username, response.data, persistent);
            this.onLogin(credentials.username);
        } catch (reason) {
            this.session.clear(CREDENTIALSKEY);
            this.session.clear(TOKENUSER);
            this.session.clear(TOKENKEY);
            this.session.clear(TOKENEXPIRE);
            this.session.clear(REFRESHKEY);
            throw reason;
        }
    }

    private logoutHandlers: ((sender: AuthenticationService) => boolean | void)[] = [];

    public addLogoutHandler(handler: (sender: AuthenticationService) => boolean | void) {
        if (!this.logoutHandlers.some(h => h === handler))
            this.logoutHandlers.push(handler);
    }

    public removeLogoutHandler(handler: (sender: AuthenticationService) => boolean | void) {
        this.logoutHandlers = this.logoutHandlers.filter(h => h !== handler);
    }

    private onLogout() {
        var handled = false;
        for (var handler of this.logoutHandlers)
            handled = handler(this) === true || handled;
        return !handled;
    }

    private loginHandlers: ((sender: AuthenticationService, username: string) => boolean | void)[] = [];

    public addLoginHandler(handler: (sender: AuthenticationService, username: string) => boolean | void) {
        if (!this.loginHandlers.some(h => h === handler))
            this.loginHandlers.push(handler);
    }

    public removeLoginHandler(handler: (sender: AuthenticationService) => boolean | void) {
        this.loginHandlers = this.loginHandlers.filter(h => h !== handler);
    }

    private onLogin(username: string) {
        var handled = false;
        for (var handler of this.loginHandlers) {
            handled = handler(this, username) === true || handled;
        }
        return !handled;
    }

    public async logout(delayEvent: number = 0) {
        this.session.clear(CREDENTIALSKEY);
        this.session.clear(TOKENUSER);
        this.session.clear(TOKENKEY);
        this.session.clear(TOKENEXPIRE);
        this.session.clear(REFRESHKEY);
        if (delayEvent)
            await this.$timeout(delayEvent);
        this.onLogout();
    }

    public get username(): string {
        var c = this.credentials;
        return c ? c.username : "";
    }

    public get token(): string {
        var c = this.credentials;
        return c && c.token ? c.token.access_token : "";
    }

    /**
     * Looksup the credentials in localStorage or sessionStorage
     *
     * returns credential meta data
     *      with refresh key
     *      optionaly if not expired with token key
     *
     * returns undefined, when no credentials are found
     *
     * @readonly
     * @private
     * @type {Credentials}
     * @memberof AuthenticationService
     */
    private get credentials(): Credentials {
        let cred = this.session.getValue<Credentials>(CREDENTIALSKEY);
        let username =  this.session.getValue<string>(TOKENUSER);
        if (cred) {
            let expire: number = this.session.getValue<number>(TOKENEXPIRE);
            if (cred.username && expire !== undefined && new Date(expire) > new Date(new Date().getTime() + REFRESH_MARGIN)) {
                if (username === undefined) {
                    this.session.setValue<string>(TOKENUSER, cred.username, false, false);
                    cred.token.access_token = this.session.getValue<string>(TOKENKEY);
                } else if (cred.username === username) {
                    cred.token.access_token = this.session.getValue<string>(TOKENKEY);
                } else {
                    this.session.clear(TOKENKEY);
                    this.session.clear(TOKENEXPIRE);
                }
            } else {
                this.session.clear(TOKENKEY);
                this.session.clear(TOKENUSER);
                this.session.clear(TOKENEXPIRE);
            }
            cred.token.refresh_token = this.session.getValue<string>(REFRESHKEY);
        }
        return cred;
    }

    private handleTokenResponse(user: string, response: AccessTokenResponse, persistent: boolean): boolean {
        if (!(response && response.access_token && response.refresh_token && response.expires_in)) {
            this.session.clear(TOKENKEY);
            this.session.clear(TOKENEXPIRE);
            this.session.clear(REFRESHKEY);
            this.session.clear(CREDENTIALSKEY);
            return false;
        }
        var expires = new Date().getTime() + (response.expires_in * 1000);

        this.session.setValue<string>(TOKENKEY, response.access_token, false, false);
        this.session.setValue<number>(TOKENEXPIRE, expires, false, false);
        this.session.setValue<string>(REFRESHKEY, response.refresh_token, false, true);
        this.session.setValue<Credentials>(CREDENTIALSKEY, {
            username: user,
            token: {
            },
            persistent: persistent
        }, false, true);
        return true;
    }

    private setTokenHeader(config: ng.IRequestConfig): ng.IRequestConfig {
        var tokendata = this.credentials != null ? this.credentials.token : null;
        if (!!tokendata && !!tokendata.access_token) {
            config.headers["Authorization"] = `Bearer ${tokendata.access_token}`;
        }
        return config;
    }

    // check credentials lifetime before api calls
    public AuthenticationSetter(config: ng.IRequestConfig): ng.IPromise<ng.IRequestConfig> {
        this.isAlive();
        return this.$q<ng.IRequestConfig>(resolver => resolver(this.setTokenHeader(config)));
    }

    // check credentials lifetime (before angular component activation, $canActivate)
    public isAlive() {
        var tokendata = this.credentials != null ? this.credentials.token : null;
        if (tokendata == null || tokendata.refresh_token == null) {
            return false;
        } else if (tokendata.access_token) {
            return true;
        } else {
            var response = this.api.SynchronousRefreshToken(tokendata.refresh_token);
            return this.handleTokenResponse(this.username, response, this.credentials.persistent);
        }
    }

    public changePassword(userName: string, oldPassword: string, newPassword: string, culture: string): ng.IPromise<string> {
        return this.api.changePassword(userName, oldPassword, newPassword, culture);
    }

    public changePasswordWithResetToken(userName: string, password: string, resetToken: string, culture: string): ng.IPromise<string> {
        return this.api.changePasswordWithToken(userName, password, resetToken, culture);
    }

    public resetPassword(emailAddress: string, resetLink: string, culture: string): ng.IPromise<string> {
        return this.api.resetPassword(emailAddress, resetLink, culture);
    }

    public static AuthenticationCheck = function AuthenticationCheck(AuthenticationService: AuthenticationService, $rootRouter: angular.Router, $location: angular.ILocationService, IsProcessingService: IsProcessingService) {
        var isAlive = AuthenticationService.isAlive();
        if (!isAlive) {
            var current = $location.url();
            if (current.indexOf("/newpassword") >= 0
                || current.indexOf("/forgotpassword") >= 0
                || current.indexOf("/login") >= 0)
                return;

            var target: any[] = ['Login'];
            if (current.indexOf("/login") == -1 && current.length > 1)
                target = target.concat({ source: encodeURIComponent($location.url()) })

            var instuction = $rootRouter.generate(target);
            IsProcessingService.reset();
            $rootRouter.navigateByInstruction(instuction);
        }
        return isAlive;
    }
}
AuthenticationService.AuthenticationCheck.$inject = ['AuthenticationService', '$rootRouter', '$location', 'IsProcessingService']


declare interface Credentials {
    username: string;
    token: {
        access_token?: string;
        refresh_token?: string;
    },
    persistent: boolean
}
